import ChevronDownIcon from '@assets/icons/chevron-down-v2.svg';
import React, { ReactNode, useEffect, useRef } from 'react';
import { Animated, Easing, Pressable } from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { SvgProps } from 'react-native-svg';
import { useHover } from 'react-native-web-hooks';

const CHEVRON_ANIMATION_DURATION_IN_MS = 250;

interface DropdownTriggerProps {
  accessibilityLabel?: string;
  children: ReactNode;
  disabled?: boolean;
  Icon?: React.FC<SvgProps>;
  isOpen: boolean;
  onPress: () => void;
  style?: object;
}

/**
 * Shared dropdown trigger button with animated chevron.
 * Used across sales portal dropdowns for consistent styling and behavior.
 */
export const DropdownTrigger: React.FC<DropdownTriggerProps> = ({
  accessibilityLabel,
  children,
  disabled,
  Icon,
  isOpen,
  onPress,
  style,
}) => {
  const [styles, theme] = useStyles();
  const chevronAnimatedValue = useRef(new Animated.Value(0)).current;
  const triggerRef = useRef<React.ComponentRef<typeof Pressable>>(null);
  const isHovered = useHover(triggerRef);
  const showHover = isHovered && !disabled;

  useEffect(() => {
    const animation = Animated.timing(chevronAnimatedValue, {
      duration: CHEVRON_ANIMATION_DURATION_IN_MS,
      easing: Easing.out(Easing.exp),
      toValue: isOpen ? 1 : 0,
      useNativeDriver: false,
    });

    animation.start();

    return () => {
      animation.stop();
    };
  }, [isOpen, chevronAnimatedValue]);

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      accessibilityState={{ disabled: disabled ?? false, expanded: isOpen }}
      aria-disabled={disabled}
      disabled={disabled}
      onPress={onPress}
      ref={triggerRef}
      style={[styles.trigger, showHover ? styles.triggerHover : null, style]}
    >
      {children}
      {Icon ? (
        <Icon
          height={16}
          stroke={
            disabled
              ? theme.sk.color.text.disabled
              : theme.sk.color.icon.primary
          }
          width={16}
        />
      ) : (
        <Animated.View
          style={{
            transform: [
              {
                rotate: chevronAnimatedValue.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0deg', '-180deg'],
                }),
              },
            ],
          }}
        >
          <ChevronDownIcon
            height={16}
            stroke={
              disabled
                ? theme.sk.color.text.disabled
                : theme.sk.color.icon.primary
            }
            width={16}
          />
        </Animated.View>
      )}
    </Pressable>
  );
};

const useStyles = createStyles((theme) => ({
  trigger: {
    alignItems: 'center',
    borderColor: theme.sk.color.border.input.default,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    justifyContent: 'space-between',
    minHeight: 42,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceXsmall,
  },
  triggerHover: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
}));
